/**
 * Process, from hung-hola blocks/process/view.js (docs/animation/process.md there):
 * - Hovering a "Gate N" tab next to a step, or gate card N itself, lights gate card N and it rises, while the
 *   tab stays put and darkens (is-active on both, process.css; round 1 #6); keyboard focus does the same. Desktop with a real pointer only (> 1280).
 * - When the steps scroll into view they draw in once (GSAP ScrollTrigger, once, no scrub; the owner dropped
 *   scrub on 2026-10-18): the six steps fade in in order 1-6 sliding out from the middle column, each with its
 *   dot / blue ray; the white line runs down at a constant speed and reaches dot N as step N shows. Then gates
 *   1-3, each as a pair: the "Gate N" tab and gate card N below.
 * - touch: (≤ 1280; no middle column): the steps slide in from the left one by one; a step with a gate shows
 *   its tab before the next step starts. Tapping "Gate N" scrolls to gate card N and lights it without rising
 *   (is-selected); tapping gate card N scrolls back up to "Gate N".
 * - tablet-v: (≤ 768): scrolling lights the step whose top has passed 50% of the viewport, one at a time.
 * - ?still and reduced motion: everything shows at once (the markup is the finished layout); the tap links
 *   and the mobile lit step stay (states, not motion).
 * GSAP + ScrollTrigger: Nectar's (handle "gsap", motionLib()).
 */
import { motionLib } from '../../assets/js/motion';

const START = 'top 80%'; // the steps' top reaches 80% of the viewport height: plays once
const DOT = 0.3; // s, dot appears with its left step
const RAYS = 0.4; // s, a blue line spreads from the dot with its step
const STEP = 0.5; // s, each step card fades in
const STEP_STAGGER = 0.25; // s between two step cards
const STEP_SHIFT = 40; // px a step card slides in from the middle column: steps 1-3 leftwards, 4-6 rightwards
const GATE_RISE = 16; // px a gate rises while it fades in
const GATE = 0.7; // s, each gate (tab next to its step + card below) fades in, after the last step
const GATE_STAGGER = 0.35; // s between gate 1, 2 and 3
const GATE_AFTER_STEP6 = 0.1; // s after step 6 (Ship) starts showing, gate 1 starts
const EASE = 'power2.out'; // dots, blue lines, steps and gates
// touch: steps slide in from the left one by one; a step with a gate shows its tab before the next step starts
const SMALL_STEP = 0.4; // s each step slides in
const SMALL_SHIFT = 40; // px a step slides in from the left
const SMALL_TAB = 0.3; // s a gate tab fades in (rising GATE_RISE) after its step
const SMALL_OVERLAP = 0.15; // s the next piece starts before the previous one has finished
const ACTIVE_AT = '50%'; // tablet-v: a step lights up once its top passes 50% of the viewport height

const DESKTOP = '(width > 1280px)';
const TOUCH = '(width <= 1280px)';
const MOBILE = '(width <= 768px)';

const isDesktop = window.matchMedia(DESKTOP);
const canHover = window.matchMedia(`(hover: hover) and ${DESKTOP}`);
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Scroll an element to the middle of the screen, with Lenis when it runs (a native scroll would be taken back). */
function scrollToCenter(el: HTMLElement): void {
	const offset = -(window.innerHeight - el.offsetHeight) / 2;
	if (window.lenis) {
		window.lenis.scrollTo(el, { offset, force: true });
		return;
	}
	el.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
}

function gates(wrapper: HTMLElement): void {
	const cards = [...wrapper.querySelectorAll<HTMLElement>('[data-process-gate]')];
	cards.forEach((gate) => {
		const tab = wrapper.querySelector<HTMLElement>(`[data-process-gate-tab="${gate.dataset.processGate}"]`);
		// touch: tapping "Gate N" scrolls to gate card N and lights it (is-selected) until another gate is tapped;
		// tapping gate card N scrolls back up to "Gate N" and turns it off. Desktop: the tab only lights (hover).
		tab?.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation(); // not Lenis's anchor glide
			if (isDesktop.matches) {
				return;
			}
			cards.forEach((g) => g.classList.toggle('is-selected', g === gate));
			scrollToCenter(gate);
		});
		gate.addEventListener('click', () => {
			if (isDesktop.matches || !tab) {
				return;
			}
			gate.classList.remove('is-selected');
			scrollToCenter(tab);
		});
		// The card and its tab light up together: the card rises, the tab darkens in place (round 1 #6)
		const pair = [gate, tab].filter((el): el is HTMLElement => !!el);
		const on = (): void => pair.forEach((p) => p.classList.add('is-active'));
		const off = (): void => pair.forEach((p) => p.classList.remove('is-active'));
		pair.forEach((el) => {
			el.addEventListener('pointerenter', () => canHover.matches && on());
			el.addEventListener('pointerleave', off);
		});
		tab?.addEventListener('focus', () => isDesktop.matches && tab.matches(':focus-visible') && on());
		tab?.addEventListener('blur', off);
	});
}

function motion(wrapper: HTMLElement): void {
	const lib = window.gsap && window.ScrollTrigger ? { gsap: window.gsap, ScrollTrigger: window.ScrollTrigger } : null;
	if (!lib || new URLSearchParams(window.location.search).has('still')) {
		return;
	}
	const { gsap, ScrollTrigger } = lib;
	gsap.registerPlugin(ScrollTrigger);

	const spine = wrapper.querySelector<HTMLElement>('[data-process-spine]');
	const line = spine?.querySelector<HTMLElement>('[data-process-spine-line]');
	const rows = [...(spine?.querySelectorAll<HTMLElement>('[data-process-spine-row]') ?? [])];
	const steps = [...wrapper.querySelectorAll<HTMLElement>('.process__step')];
	const trigger = spine?.parentElement;
	// gsap.matchMedia reverts every inline style when the screen crosses a breakpoint
	const media = gsap.matchMedia();

	// tablet-v: the step whose top has passed ACTIVE_AT lights up like its hover (owner, 2026-10-19). Each step owns
	// the scroll from its top to the next step's top, so one is lit at a time. A state, not motion: kept with
	// reduced motion.
	media.add(MOBILE, () => {
		steps.forEach((step, k) => {
			const card = step.querySelector('.process__step-card');
			if (!card) {
				return;
			}
			const next = steps[k + 1];
			ScrollTrigger.create({
				trigger: step,
				start: `top ${ACTIVE_AT}`,
				endTrigger: next ?? step,
				end: `${next ? 'top' : 'bottom'} ${ACTIVE_AT}`,
				toggleClass: { targets: card, className: 'is-active' },
			});
		});
	});

	// The draw-in: not with reduced motion (motionLib() is null then)
	if (!motionLib() || !line || !rows.length || !trigger) {
		return;
	}

	media.add(TOUCH, () => {
		const timeline = gsap.timeline({
			defaults: { ease: EASE, clearProps: 'all' },
			scrollTrigger: { trigger, start: START, once: true },
		});
		steps.forEach((step, k) => {
			timeline.from(step, { autoAlpha: 0, x: -SMALL_SHIFT, duration: SMALL_STEP }, k ? `-=${SMALL_OVERLAP}` : 0);
			const tab = step.querySelector('[data-process-gate-tab]');
			if (tab) {
				timeline.from(tab, { autoAlpha: 0, y: GATE_RISE, duration: SMALL_TAB }, `-=${SMALL_OVERLAP}`);
			}
		});
	});

	media.add(DESKTOP, () => {
		const timeline = gsap.timeline({
			// clearProps: once a piece has shown, drop GSAP's inline styles (its inline `translate: none` would beat
			// the hover's `translate` in process.css: the gate tab rising with its card)
			defaults: { ease: EASE, clearProps: 'all' },
			scrollTrigger: { trigger, start: START, once: true },
		});
		// Step k (0-5) shows at (k + 0.5) * STEP_STAGGER. Rows share the column height equally (dot i at (2i + 1) / 2n
		// of the line), so a line running n * STEP_STAGGER at constant speed reaches dot i as left step i shows.
		const at = (k: number): number => (k + 0.5) * STEP_STAGGER;
		timeline.from(line, { scaleY: 0, transformOrigin: 'center top', duration: rows.length * STEP_STAGGER, ease: 'none' }, 0);
		// Steps 1-6 in order; the left column holds steps 1-3 (row i = step i), the right one steps 4-6 (row i = step i + n)
		steps.forEach((step, k) => {
			const row = rows[k % rows.length];
			const left = k < rows.length;
			timeline.from(step, { autoAlpha: 0, x: left ? STEP_SHIFT : -STEP_SHIFT, duration: STEP }, at(k));
			if (!row) {
				return;
			}
			const dot = row.querySelector('[data-process-spine-dot]');
			if (left && dot) {
				timeline.from(dot, { scale: 0, duration: DOT }, at(k));
			}
			const ray = row.querySelector(`[data-process-ray="${left ? 'left' : 'right'}"]`);
			if (ray) {
				timeline.from(ray, { scaleX: 0, transformOrigin: left ? 'right center' : 'left center', duration: RAYS }, at(k));
			}
		});
		// Then the gates, 1 to 3: tab N next to its step and gate card N below together. The card's list item moves,
		// not the card: the card's own opacity and translate belong to the hover.
		const pairs = [...wrapper.querySelectorAll<HTMLElement>('[data-process-gate]')].map((gate) =>
			[wrapper.querySelector(`[data-process-gate-tab="${gate.dataset.processGate}"]`), gate.parentElement].filter(
				(el): el is HTMLElement => !!el,
			),
		);
		timeline.addLabel('gates', at(steps.length - 1) + GATE_AFTER_STEP6);
		pairs.forEach((pair, n) => {
			timeline.from(pair, { autoAlpha: 0, y: GATE_RISE, duration: GATE }, `gates+=${n * GATE_STAGGER}`);
		});
	});
}

document.querySelectorAll<HTMLElement>('[data-px-block="process"]').forEach((wrapper) => {
	gates(wrapper);
	motion(wrapper);
});
